import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Button, Modal, Space, Input, Switch, Tooltip, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ResourcePolicyEditor, createAuthApi } from '@zero/auth-admin-ui';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';
import { RowActions } from '@fms/shared';

export function PoliciesPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const [policies, setPolicies]     = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [editing, setEditing]       = useState(null);    // null = closed | { ...policy } = edit | {} = create new
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize]     = useGridPageSize('policies', 25);

  const refresh = async () => {
    setLoading(true);
    try {
      const [pol, perms] = await Promise.all([
        api.listAllPolicies(),
        api.listPermissions(),
      ]);
      setPolicies(pol.data?.Data ?? []);
      setPermissions(perms.data?.Data ?? []);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!filterText) return policies;
    const q = filterText.toLowerCase();
    return policies.filter((p) =>
      [p.ResourceType, p.Action, p.PolicyName, p.RuleType, p.Effect]
        .some((v) => v?.toLowerCase().includes(q))
    );
  }, [policies, filterText]);

  const handleSave = async (dto) => {
    try {
      if (editing?.PolicyID) {
        await api.updatePolicy(editing.PolicyID, dto);
      } else {
        await api.createPolicy(dto);
      }
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      setEditing(null);
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deletePolicy(id);
      message.success(t('common.deleted'));
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const toggleActive = async (record, isActive) => {
    try {
      await api.updatePolicy(record.PolicyID, { ...record, IsActive: isActive });
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const columns = [
    { title: t('common.id'), dataIndex: 'PolicyID', width: 70 },
    { title: t('policy.resourceType'), dataIndex: 'ResourceType', width: 200,
      render: (v) => <Tag color="processing" style={{ fontFamily: 'Consolas, monospace', fontSize: 11 }}>{v}</Tag> },
    { title: t('policy.action'), dataIndex: 'Action', width: 110,
      render: (v) => <Tag>{v}</Tag> },
    { title: t('policy.name'), dataIndex: 'PolicyName',
      render: (v) => <strong>{v}</strong> },
    { title: t('policy.ruleType'), dataIndex: 'RuleType', width: 140,
      render: (v) => <Tag color="default">{v}</Tag> },
    { title: t('policy.effect'), dataIndex: 'Effect', width: 100,
      render: (v) => <Tag color={v === 'Deny' ? 'red' : 'green'}>{t(`policy.effect.${v.toLowerCase()}`, v)}</Tag> },
    { title: t('policy.priority'), dataIndex: 'Priority', width: 90, align: 'right',
      sorter: (a, b) => a.Priority - b.Priority },
    { title: t('policy.active'), dataIndex: 'IsActive', width: 90, align: 'center',
      render: (v, r) => <Switch size="small" checked={v} onChange={(c) => toggleActive(r, c)} /> },
    { title: t('common.actions'), key: 'actions', width: 90, align: 'center',
      render: (_, r) => (
        <RowActions items={[
          { key: 'edit', label: t('common.edit'), onClick: () => setEditing(r) },
          { key: 'delete', label: t('common.delete'), danger: true,
            onClick: () => modal.confirm({
              title:    t('policy.deleteConfirmTitle'),
              content:  t('policy.deleteConfirmContent', { name: r.PolicyName }),
              icon:     <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
              okText:   t('common.delete'),
              okButtonProps: { danger: true },
              cancelText: t('common.cancel'),
              onOk:     () => handleDelete(r.PolicyID),
            }) },
        ]} />
      ) },
  ];

  return (
    <Card
      size="small"
      extra={
        <Space size={4}>
          <Input.Search
            allowClear
            size="small"
            placeholder={t('common.search')}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ width: 220 }}
          />
          <Button size="small" icon={<ReloadOutlined />} onClick={refresh} />
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
            {t('common.createNew')}
          </Button>
        </Space>
      }
    >
      <Table
        dataSource={filtered}
        columns={columns}
        loading={loading}
        rowKey="PolicyID"
        size="small"
        bordered
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
        scroll={{ y: 'calc(100vh - 200px)' }}
      />

      <Modal maskClosable={false}
        open={editing !== null}
        onCancel={() => setEditing(null)}
        footer={null}
        title={editing?.PolicyID ? t('policy.edit') : t('policy.create')}
        width={900}
        destroyOnHidden
      >
        <ResourcePolicyEditor
          policy={editing?.PolicyID ? editing : null}
          permissions={permissions}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          bare
        />
      </Modal>
    </Card>
  );
}
